<script setup>
import { ref, reactive, computed, onMounted, onBeforeUnmount } from 'vue'
import Pagination from 'v-pagination-3'
import VueMultiselect from 'vue-multiselect'
import DateRangePicker from 'vue3-daterange-picker'
import {useI18n} from 'vue-i18n'
import {useRouter} from 'vue-router'
import $http from '@/axios.config'
import helpers from '../../helpers.js'
import store from '@/store'
import {log} from "@/utils/clientLogger.js";
import Swal from 'sweetalert2'

const {t} = useI18n({useScope: 'global'})
const router = useRouter()

const userRole = computed(() => store.getters.userData?.role?.name)
const isDistributor = computed(() => userRole.value === 'distributor')
const isAdmin = computed(() => userRole.value === 'admin')

const currentPage = ref(1)
const perPage = 50
const totalItems = ref(0)
const ordersData = ref([])

const clientData = ref(null)
const managerData = ref(null)
const draftsmanData = ref(null)

const tempClients = ref([])

const filteredClients = computed(() => {
  if (isDistributor.value) {
    return tempClients.value.filter(client => client.accessStatus !== 'pending');
  }
  return tempClients.value;
});

const tempManagers = ref([])
const tempDraftsmen = ref([])

const isLoadingClients = ref(false)
const isLoadingManagers = ref(false)
const isLoadingDraftsmen = ref(false)

let clientSearchTimer = null
let managerSearchTimer = null
let draftsmanSearchTimer = null

const humanId = ref('')
const title = ref('')
const firstOrderDate = ref(null)

const hasProblematicPattern = ref(false)
const hasProblematicPackaging = ref(false)
const wasCut = ref(false)
const noPatternsFilter = ref('')
const assemblyProgramFilters = ref([])
const assemblyPrograms = helpers.assemblyPrograms
const isProgramFilterOpen = ref(false)
const toggleProgramFilter = () => { isProgramFilterOpen.value = !isProgramFilterOpen.value }
const closeProgramFilter = () => { isProgramFilterOpen.value = false }

const dateRange = ref({
  startDate: new Date(new Date().setDate(new Date().getDate() - 1)),
  endDate: new Date(),
})

const localData = reactive({
  direction: 'ltr',
  format: 'dd-mm-yyyy',
  separator: '-',
  applyLabel: t('buttons.apply'),
  cancelLabel: t('buttons.cancel'),
  weekLabel: 'Т',
  customRangeLabel: t('labels.customPeriod'),
  daysOfWeek: [t('days.sunday'), t('days.monday'), t('days.tuesday'), t('days.wednesday'), t('days.thursday'), t('days.friday'), t('days.saturday')],
  monthNames: [t('months.january'), t('months.february'), t('months.march'), t('months.april'), t('months.may'), t('months.june'), t('months.july'), t('months.august'), t('months.september'), t('months.october'), t('months.november'), t('months.december')],
  firstDay: 0,
})

const fetchFirstOrderDate = async () => {
  try {
    const res = await $http.get('/api/orders/first-date')
    const firstDate = new Date(res.data.date)
    firstDate.setHours(0, 0, 0, 0)
    firstOrderDate.value = firstDate
    return firstDate
  } catch (error) {
    log.error('View:OrdersPage:fetchFirstOrderDate', {'error': error})
    return null
  }
}

const sort = reactive({
  by: 'createdAt',
  order: 'desc'
});

const sortBy = (column) => {
  if (sort.by === column) {
    sort.order = sort.order === 'asc' ? 'desc' : 'asc';
  } else {
    sort.by = column;
    sort.order = 'desc';
  }
  getOrders();
};

const getOrders = async () => {
  const filter = {
    humanId: humanId.value.toUpperCase(),
    title: title.value,
    clientId: clientData.value?._id,
    managerId: managerData.value?._id,
    draftsmanId: draftsmanData.value?._id,
  }

  if (hasProblematicPattern.value) {
    filter.hasProblematicPattern = true
  }
  if (hasProblematicPackaging.value) {
    filter.hasProblematicPackaging = true
  }
  if (wasCut.value) {
    filter.wasCut = true
  }
  if (noPatternsFilter.value === 'any') {
    filter.hasNoPatterns = true
  } else if (['none_in_any', 'none_in_ours', 'partial'].includes(noPatternsFilter.value)) {
    filter.noPatternsType = noPatternsFilter.value
  }
  if (assemblyProgramFilters.value.length) {
    filter.assemblyPrograms = assemblyProgramFilters.value
  }

  if (dateRange.value.startDate instanceof Date && !isNaN(dateRange.value.startDate)) {
    filter.startDate = dateRange.value.startDate
  }
  if (dateRange.value.endDate instanceof Date && !isNaN(dateRange.value.endDate)) {
    filter.endDate = dateRange.value.endDate
  }

  try {
    const response = await $http.get(`api/orders/`, {
      params: {
        page: currentPage.value,
        perPage,
        filter,
        sort: sort
      }
    })

    ordersData.value = response.data.ordersData
    totalItems.value = response.data.totalOrders
    currentPage.value = response.data.currentPage
  } catch (e) {
    log.error('View:OrdersPage:getOrders', {error: e})
  }
}

const dateToLocale = (date) => helpers.convertTime(date)
const assemblyProgramLabels = (values) => helpers.getAssemblyProgramLabels(values)

const asyncFindClient = (query) => {
  isLoadingClients.value = true;
  if (clientSearchTimer) clearTimeout(clientSearchTimer);

  clientSearchTimer = setTimeout(async () => {
    try {
      const params = { name: query || '', limit: 50 };
      const res = await $http.get('/api/clients/', {params});

      if (res.data && Array.isArray(res.data.clientData)) {
        tempClients.value = res.data.clientData.slice(0, 50);
      } else {
        tempClients.value = [];
      }
    } catch (e) {
      log.error('OrdersPage:asyncFindClient', {error: e});
      tempClients.value = [];
    } finally {
      isLoadingClients.value = false;
    }
  }, 300);
}

const asyncFindUser = (query, type) => {
  if (type === 'manager') isLoadingManagers.value = true;
  if (type === 'draftsman') isLoadingDraftsmen.value = true;

  if (type === 'manager' && managerSearchTimer) clearTimeout(managerSearchTimer);
  if (type === 'draftsman' && draftsmanSearchTimer) clearTimeout(draftsmanSearchTimer);

  const performSearch = async () => {
    try {
      const params = { type, name: query || '', limit: 50 };
      const res = await $http.get('/api/users/', { params });

      const results = (res.data && Array.isArray(res.data.data)) ? res.data.data.slice(0, 50) : [];

      if (type === 'manager') {
        tempManagers.value = results;
      } else if (type === 'draftsman') {
        tempDraftsmen.value = results;
      }
    } catch (e) {
      log.error(`OrdersPage:asyncFindUser:${type}`, {error: e});
    } finally {
      if (type === 'manager') isLoadingManagers.value = false;
      if (type === 'draftsman') isLoadingDraftsmen.value = false;
    }
  };

  if (type === 'manager') {
    managerSearchTimer = setTimeout(performSearch, 300);
  } else {
    draftsmanSearchTimer = setTimeout(performSearch, 300);
  }
}

const isOrderFromDistributor = (order) => {
  return order?.managerId?.role?.name === 'distributor'
}

const onOpenClients = () => {
  if (tempClients.value.length === 0) asyncFindClient('');
}
const onOpenManagers = () => {
  if (tempManagers.value.length === 0) asyncFindUser('', 'manager');
}
const onOpenDraftsmen = () => {
  if (tempDraftsmen.value.length === 0) asyncFindUser('', 'draftsman');
}


const setDataRange = (range) => {
  const startDate = new Date(range.startDate)
  const endDate = new Date(range.endDate)
  startDate.setHours(0, 0, 0, 0)
  endDate.setHours(23, 59, 59, 999)
  dateRange.value = {startDate, endDate}
  getOrders()
}

const getOrdersByHumanId = (val) => {
  if (val.length < 2) return
  cleanDataRange()
  getOrders()
}

const cleanDataRange = () => {
  dateRange.value.startDate = null
  dateRange.value.endDate = null
}

const makeCustomRanges = computed(() => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const yesterday = new Date(today)
  yesterday.setDate(today.getDate() - 1)
  return {
    [t('ranges.today')]: [today, today],
    [t('ranges.yesterday')]: [yesterday, yesterday],
    [t('ranges.last7')]: [new Date(today.getFullYear(), today.getMonth(), today.getDate() - 6), today],
    [t('ranges.thisMonth')]: [new Date(today.getFullYear(), today.getMonth(), 1), today],
    [t('ranges.lastMonth')]: [new Date(today.getFullYear(), today.getMonth() - 1, 1), new Date(today.getFullYear(), today.getMonth(), 0)],
    [t('ranges.thisYear')]: [new Date(today.getFullYear(), 0, 1), new Date(today.getFullYear(), 11, 31)],
    ...(firstOrderDate.value ? {[t('ranges.allTime')]: [firstOrderDate.value, today]} : {})
  }
})

const cloneOrder = (id) => {
  Swal.fire({
    title: t('buttons.clone'),
    text: t('asks.confirmClone', 'Ви дійсно хочете клонувати це замовлення?'),
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: t('buttons.confirm'),
    cancelButtonText: t('buttons.cancel'),
    showLoaderOnConfirm: true,
    preConfirm: () => {
      return $http.post(`/api/orders/clone/${id}`)
          .then(response => response.data)
          .catch(error => {
            Swal.showValidationMessage(`Request failed: ${error}`);
          });
    },
    allowOutsideClick: () => !Swal.isLoading()
  }).then((result) => {
    if (result.isConfirmed) {
      Swal.fire(t('alerts.success'), t('messages.orderCloned', 'Замовлення успішно клоновано'), 'success');
      router.push(`/order/${result.value._id}`);
    }
  });
}

onMounted(async () => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const end = new Date()
  end.setHours(23, 59, 59, 999)
  dateRange.value.startDate = today
  dateRange.value.endDate = end

  await Promise.all([
    fetchFirstOrderDate(),
    getOrders()
  ]);

  asyncFindClient('');
  asyncFindUser('', 'manager');
  asyncFindUser('', 'draftsman');

  document.addEventListener('click', closeProgramFilter);
})

onBeforeUnmount(() => {
  document.removeEventListener('click', closeProgramFilter);
})
</script>

<template>
  <div class="row align-items-center">
    <div class="col-12 mt-3">
      <div class="card border-0 shadow-sm filters-card">
        <div class="card-body py-3">
          <div class="row g-2 align-items-end justify-content-center">
            <div class="col-6 col-md-4 col-lg-3">
              <label class="form-label small text-muted mb-1">{{ t('placeholders.clientName') }}</label>
      <VueMultiselect
          v-model="clientData"
          :close-on-select="true"
          :clear-on-select="false"
          :preserve-search="true"
          :options="filteredClients"
          :show-labels="false"
          :internal-search="false"
          :loading="isLoadingClients"
          @select="getOrders"
          @remove="getOrders"
          @search-change="asyncFindClient"
          @open="onOpenClients"
          :placeholder="t('placeholders.clientName')"
          label="name"
          track-by="name"
      >
        <template #noResult>
          {{ t('messages.noResults', 'Нічого не знайдено. Спробуйте змінити запит.') }}
        </template>
        <template #afterList>
          <div class="p-2 text-center text-muted" style="font-size: 12px; opacity: 0.7;">
            <span v-if="tempClients.length >= 50">
              {{ t('messages.firstSearchResults', 'Показано перші 50 результатів. Продовжуйте писати для уточнення...') }}
            </span>
          </div>
        </template>
      </VueMultiselect>
            </div>

            <div class="col-6 col-md-4 col-lg-3" v-if="!isDistributor">
              <label class="form-label small text-muted mb-1">{{ t('placeholders.managerName') }}</label>
      <VueMultiselect
          v-model="managerData"
          :close-on-select="true"
          :clear-on-select="false"
          :preserve-search="true"
          :show-labels="false"
          :options="tempManagers"
          :internal-search="false"
          :loading="isLoadingManagers"
          @select="getOrders"
          @remove="getOrders"
          @search-change="asyncFindUser($event, 'manager')"
          @open="onOpenManagers"
          :placeholder="t('placeholders.managerName')"
          label="username"
          track-by="username"
      >
        <template #noResult>
          {{ t('messages.noResults', 'Нічого не знайдено. Спробуйте змінити запит.') }}
        </template>
        <template #afterList>
          <div class="p-2 text-center text-muted" style="font-size: 12px; opacity: 0.7;">
            <span v-if="tempManagers.length >= 50">
               {{ t('messages.firstSearchResults', 'Показано перші 50 результатів. Продовжуйте писати для уточнення...') }}
            </span>
          </div>
        </template>
      </VueMultiselect>
            </div>

            <div class="col-6 col-md-4 col-lg-3">
              <label class="form-label small text-muted mb-1">{{ t('placeholders.DraftsManName') }}</label>
      <VueMultiselect
          v-model="draftsmanData"
          :close-on-select="true"
          :clear-on-select="false"
          :preserve-search="true"
          :options="tempDraftsmen"
          :show-labels="false"
          :internal-search="false"
          :loading="isLoadingDraftsmen"
          @select="getOrders"
          @remove="getOrders"
          @search-change="asyncFindUser($event, 'draftsman')"
          @open="onOpenDraftsmen"
          :placeholder="t('placeholders.DraftsManName')"
          label="username"
          track-by="username"
      >
        <template #noResult>
          {{ t('messages.noResults', 'Нічого не знайдено. Спробуйте змінити запит.') }}
        </template>
        <template #afterList>
          <div class="p-2 text-center text-muted" style="font-size: 12px; opacity: 0.7;">
            <span v-if="tempDraftsmen.length >= 50">
               {{ t('messages.firstSearchResults', 'Показано перші 50 результатів. Продовжуйте писати для уточнення...') }}
            </span>
          </div>
        </template>
      </VueMultiselect>
            </div>

            <div class="col-6 col-md-4 col-lg-2">
              <label class="form-label small text-muted mb-1">ID</label>
              <input type="text"
                     class="form-control"
                     placeholder="ID"
                     @input="getOrdersByHumanId($event.target.value)"
                     v-model="humanId"
              >
            </div>
            <div class="col-6 col-md-4 col-lg-3">
              <label class="form-label small text-muted mb-1">{{ t('tableheadings.title', 'Марка / Модель') }}</label>
              <input type="text"
                     class="form-control"
                     placeholder="Марка / Модель авто"
                     @input="getOrders"
                     v-model="title"
              >
            </div>
            <div class="col-12 col-md-6 col-lg-4">
              <label class="form-label small text-muted mb-1">{{ t('headings.ordersForPeriod') }}</label>
              <DateRangePicker
                  ref="picker"
                  :ranges="makeCustomRanges"
                  :auto-apply="true"
                  :locale-data="localData"
                  :date-range="dateRange"
                  @select="setDataRange"
                  class="w-100"
              >
              </DateRangePicker>
            </div>
          </div>

          <hr class="my-3">

          <div class="row g-3 justify-content-center">
            <div class="col-12">
              <div class="d-flex flex-wrap gap-3 align-items-center justify-content-center">
                <div class="form-check mb-0">
                  <input class="form-check-input border-danger" type="checkbox" id="filterProblemPattern"
                         v-model="hasProblematicPattern" @change="getOrders">
                  <label class="form-check-label text-danger fw-bold" for="filterProblemPattern">
                    {{ t('labels.problemPattern', 'Тільки проблемні викройки') }}
                  </label>
                </div>
                <div class="form-check mb-0">
                  <input class="form-check-input border-danger" type="checkbox" id="filterProblemPackaging"
                         v-model="hasProblematicPackaging" @change="getOrders">
                  <label class="form-check-label text-danger fw-bold" for="filterProblemPackaging">
                    {{ t('labels.problemPackaging', 'Тільки проблеми упаковки') }}
                  </label>
                </div>
                <div class="form-check mb-0">
                  <input class="form-check-input border-info" type="checkbox" id="filterWasCut"
                         v-model="wasCut" @change="getOrders">
                  <label class="form-check-label text-info fw-bold" for="filterWasCut">
                    {{ t('labels.wasCut', 'Тільки порізані лекала') }}
                  </label>
                </div>
              </div>
            </div>

            <div class="col-6 col-md-4 col-lg-3">
              <label class="form-label small text-muted mb-1">{{ t('labels.filterNoPatternsLabel', 'Наявність лекал') }}</label>
              <select class="form-select"
                      v-model="noPatternsFilter" @change="getOrders">
                <option value="">{{ t('labels.filterNoPatternsAllOrders', 'Лекала: всі замовлення') }}</option>
                <option value="any">{{ t('labels.filterNoPatternsAny', 'Немає лекал (всі)') }}</option>
                <option value="none_in_any">{{ t('labels.noPatternsNoneInAny', 'Немає лекал в жодній з баз') }}</option>
                <option value="none_in_ours">{{ t('labels.noPatternsNoneInOurs', 'Немає лекал в наших базах') }}</option>
                <option value="partial">{{ t('labels.filterNoPatternsPartial', 'Є лекала частково') }}</option>
              </select>
            </div>
            <div class="col-6 col-md-4 col-lg-3">
              <label class="form-label small text-muted mb-1">{{ t('labels.assemblyProgram', 'Програма збірки лекала') }}</label>
              <div class="dropdown assembly-program-filter" @click.stop>
                <button class="form-select text-start text-truncate" type="button"
                        @click="toggleProgramFilter" :aria-expanded="isProgramFilterOpen">
                  <template v-if="assemblyProgramFilters.length">{{ assemblyProgramLabels(assemblyProgramFilters) }}</template>
                  <span v-else class="text-muted">{{ t('labels.filterAssemblyProgramAll', 'Програма збірки: всі') }}</span>
                </button>
                <ul class="dropdown-menu w-100 p-2 shadow-sm" :class="{ show: isProgramFilterOpen }">
                  <li v-for="program in assemblyPrograms" :key="program.value">
                    <label class="dropdown-item d-flex align-items-center gap-2 px-2 py-1 rounded">
                      <input type="checkbox" class="form-check-input m-0"
                             :value="program.value" v-model="assemblyProgramFilters" @change="getOrders">
                      <span>{{ program.label }}</span>
                    </label>
                  </li>
                  <li v-if="assemblyProgramFilters.length"><hr class="dropdown-divider"></li>
                  <li v-if="assemblyProgramFilters.length">
                    <button type="button" class="btn btn-sm btn-link text-decoration-none px-2"
                            @click="assemblyProgramFilters = []; getOrders()">
                      {{ t('buttons.clearFilter', 'Очистити') }}
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="col-12 my-5">
      <table class="table table-striped">
        <thead>
        <tr>
          <th scope="col" @click="sortBy('humanId')" class="sortable">
            ID
            <span v-if="sort.by === 'humanId'">{{ sort.order === 'asc' ? '▲' : '▼' }}</span>
          </th>
          <th scope="col" @click="sortBy('title')" class="sortable">
            {{ t('tableheadings.title') }}
            <span v-if="sort.by === 'title'">{{ sort.order === 'asc' ? '▲' : '▼' }}</span>
          </th>
          <th scope="col">{{ t('tableheadings.client') }}</th>
          <th scope="col" v-if="!isDistributor">{{ t('tableheadings.manager') }}</th>
          <th scope="col">{{ t('tableheadings.draftsman') }}</th>
          <th scope="col" @click="sortBy('createdAt')" class="sortable">
            {{ t('tableheadings.added') }}
            <span v-if="sort.by === 'createdAt'">{{ sort.order === 'asc' ? '▲' : '▼' }}</span>
          </th>
          <th scope="col" @click="sortBy('updatedAt')" class="sortable">
            {{ t('tableheadings.edited') }}
            <span v-if="sort.by === 'updatedAt'">{{ sort.order === 'asc' ? '▲' : '▼' }}</span>
          </th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="(order,index) in ordersData" :key="index">
          <th scope="row">{{ order?.humanId }}</th>
          <td>
            <router-link class="navbar-brand" target="_blank" :to="'/order/' + order._id">
              <svg xmlns="http://www.w3.org/2000/svg" class="icon icon-tabler icon-tabler-external-link"
                   width="22" height="22" viewBox="0 0 24 24" stroke-width="1.5" stroke="#9e9e9e" fill="none"
                   stroke-linecap="round" stroke-linejoin="round">
                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                <path d="M12 6h-6a2 2 0 0 0 -2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-6"/>
                <path d="M11 13l9 -9"/>
                <path d="M15 4h5v5"/>
              </svg>
            </router-link>

            <button v-if="isDistributor || isAdmin" class="btn btn-link p-0 ms-2" @click="cloneOrder(order._id)" :title="t('buttons.clone')">
              <svg xmlns="http://www.w3.org/2000/svg" class="icon icon-tabler icon-tabler-copy" width="22" height="22" viewBox="0 0 24 24" stroke-width="1.5" stroke="#9e9e9e" fill="none" stroke-linecap="round" stroke-linejoin="round">
                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                <path d="M8 8m0 2a2 2 0 0 1 2 -2h8a2 2 0 0 1 2 2v8a2 2 0 0 1 -2 2h-8a2 2 0 0 1 -2 -2z" />
                <path d="M16 8v-2a2 2 0 0 0 -2 -2h-8a2 2 0 0 0 -2 2v8a2 2 0 0 0 2 2h2" />
              </svg>
            </button>
            <span v-if="order.title">
              {{ order.title }}
            </span>
            <span v-else>
              {{ order.carBrand }} {{ order.carModel }} {{ order.carYear }}
            </span>

            <div class="mt-1 d-flex flex-wrap gap-1">
              <span v-if="order.hasProblematicPattern" class="badge d-inline-flex align-items-center gap-1 bg-warning text-dark">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="me-1">
              <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
              <path d="M12 9v4" />
              <path d="M12 16v.01" />
              <path d="M10.363 3.591l-8.106 13.534a1.914 1.914 0 0 0 1.636 2.871h16.214a1.914 1.914 0 0 0 1.636 -2.87l-8.106 -13.536a1.914 1.914 0 0 0 -3.274 0z" />
            </svg>
            {{ t('labels.problemPattern', 'Проблемна викройка') }}
          </span>
              <span v-if="order.hasProblematicPackaging" class="badge d-inline-flex align-items-center gap-1 bg-warning text-dark">
             <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="me-1">
              <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
              <path d="M12 9v4" />
              <path d="M12 16v.01" />
              <path d="M10.363 3.591l-8.106 13.534a1.914 1.914 0 0 0 1.636 2.871h16.214a1.914 1.914 0 0 0 1.636 -2.87l-8.106 -13.536a1.914 1.914 0 0 0 -3.274 0z" />
            </svg>
            {{ t('labels.problemPackaging', 'Проблема упаковки') }}
          </span>
              <span v-if="order.wasCut && (order.status === 'done' || order.status === 'closed')" class="badge d-inline-flex align-items-center gap-1 bg-info text-dark">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="me-1">
              <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
              <path d="M9.15 14.85l8.85 -10.85" />
              <path d="M7 17m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0" />
              <path d="M17 17m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0" />
              <path d="M9.15 9.15l8.85 10.85" />
            </svg>
            {{ t('log.fields.wasCutBadge', 'Різалось') }}
          </span>
              <span v-if="order.assemblyPrograms && order.assemblyPrograms.length" class="badge bg-primary text-white mw-100 text-wrap text-break text-start d-inline-flex align-items-center"
                    :title="t('labels.assemblyProgram', 'Програма збірки лекала')">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="me-1" style="vertical-align:-0.15em;">
              <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
              <path d="M4 7V4a1 1 0 0 1 1 -1h2a1 1 0 0 1 1 1v3a1 1 0 0 1 -1 1h-2a1 1 0 0 1 -1 -1z" />
              <path d="M16 7V4a1 1 0 0 1 1 -1h2a1 1 0 0 1 1 1v3a1 1 0 0 1 -1 1h-2a1 1 0 0 1 -1 -1z" />
              <path d="M4 17v-3a1 1 0 0 1 1 -1h2a1 1 0 0 1 1 1v3a1 1 0 0 1 -1 1h-2a1 1 0 0 1 -1 -1z" />
              <path d="M16 17v-3a1 1 0 0 1 1 -1h2a1 1 0 0 1 1 1v3a1 1 0 0 1 -1 1h-2a1 1 0 0 1 -1 -1z" />
            </svg>{{ t('labels.assembledIn', 'Зібрано') }}: {{ assemblyProgramLabels(order.assemblyPrograms) }}
          </span>
              <span v-if="order.hasNoPatterns && order.noPatternsType === 'partial'" class="badge d-inline-flex align-items-center gap-1 bg-warning text-dark">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="me-1">
              <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
              <path d="M4 7h3a1 1 0 0 0 1 -1v-1a2 2 0 0 1 4 0v1a1 1 0 0 0 1 1h3a1 1 0 0 1 1 1v3a1 1 0 0 0 1 1h1a2 2 0 0 1 0 4h-1a1 1 0 0 0 -1 1v3a1 1 0 0 1 -1 1h-3a1 1 0 0 1 -1 -1v-1a2 2 0 0 0 -4 0v1a1 1 0 0 1 -1 1h-3a1 1 0 0 1 -1 -1v-3a1 1 0 0 1 1 -1h1a2 2 0 0 0 0 -4h-1a1 1 0 0 1 -1 -1v-3a1 1 0 0 1 1 -1" />
            </svg>
            {{ t('labels.noPatternsPartialBadge', 'Є частково') }}
          </span>
              <span v-else-if="order.hasNoPatterns" class="badge d-inline-flex align-items-center gap-1 bg-secondary text-white"
                    :style="order.patternRequestSent ? 'background-color:#f15a24 !important;' : ''">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="me-1">
              <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
              <path d="M3 3l18 18" />
              <path d="M4 7v-2a1 1 0 0 1 1 -1h2" />
              <path d="M4 17v2a1 1 0 0 0 1 1h2" />
              <path d="M16 4h3a1 1 0 0 1 1 1v3" />
              <path d="M20 16v3a1 1 0 0 1 -1 1h-3" />
            </svg>
            {{ order.noPatternsType === 'none_in_ours' ? t('labels.noPatternsNoneInOurs', 'Немає лекал в наших базах') : t('labels.noPatternsNoneInAny', 'Немає лекал в жодній з баз') }}<template v-if="order.patternRequestSent"> · {{ t('labels.patternRequestSent', 'Відправлено запит на отримання лекал') }}</template>
          </span>
            </div>
          </td>
          <td>{{ order?.clientId?.name }}</td>

          <td v-if="!isDistributor">
            <span v-if="isOrderFromDistributor(order)"
                  class="badge bg-danger me-1"
                  style="font-size: 0.7em; text-transform: uppercase;">
              {{ t('tableheadings.distributor') }}
            </span>
            {{ order?.managerId?.username }}
          </td>

          <td>{{ order?.draftsmanId?.username }}</td>
          <td>{{ dateToLocale(order?.createdAt) }}</td>
          <td>{{ dateToLocale(order?.updatedAt) }}</td>
        </tr>


        </tbody>
      </table>
    </div>
    <pagination v-model="currentPage"
                :records="totalItems"
                :per-page="perPage"
                :options="{
                 texts: {
                   count: 'Показано з {from} по {to} всього {count} замовлень',
                   first: 'Перша',
                   last: 'Остання',
                   prev: 'Попередня',
                   next: 'Наступна',
                   theme: 'bootstrap4'
                 }
                 }"
                @paginate="getOrders"/>
  </div>
</template>

<style scoped lang="scss">
svg {
  cursor: pointer;
}

.filters-card {
  background: #f8f9fa;

  .form-label {
    font-weight: 500;
  }

  // Менший шрифт підписів чекбоксів-фільтрів
  .form-check-label {
    font-size: 0.8rem;
  }

  // Єдина висота для ВСІХ контролів фільтра (інпути, селекти, мультиселекти, пікер),
  // щоб рядок був рівний і контроли не «стрибали» по висоті.
  // vue-multiselect уже рендерить .multiselect__tags заввишки 40px — підганяємо решту під нього.
  $filter-control-height: 40px;

  :deep(.form-control),
  :deep(.form-select),
  :deep(.multiselect__tags) {
    min-height: $filter-control-height;
  }

  :deep(.vue-daterange-picker) {
    width: 100%;

    .reportrange-text {
      min-height: $filter-control-height;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0;
    }
  }
}

.vue-daterange-picker {
  width: 100%;
  text-align: center;
}

.sortable {
  cursor: pointer;
  user-select: none;
}

.sortable:hover {
  background-color: #f2f2f2;
}
</style>